import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Activity,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  Scale,
  Ruler,
  Heart,
  ChevronRight,
  Trash2,
  Clock,
  Table2,
  LineChart as LineChartIcon,
  LayoutGrid,
  Target,
  Calendar,
  ArrowUp,
  ArrowDown,
  Minus,
} from "lucide-react";
import { API } from "@/App";
import { format, parseISO, differenceInDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function MeasurementsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "evaluations"]);
  const { getLocalizedMeasurement } = useLocalizedData();

  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedParam, setSelectedParam] = useState("");
  const [selectedChartParam, setSelectedChartParam] = useState(""); // Selected measurement for chart
  const [measurementParams, setMeasurementParams] = useState([]);
  const [viewMode, setViewMode] = useState("timeline"); // timeline | table | graph | progress
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [measurementValue, setMeasurementValue] = useState("");

  useEffect(() => {
    if (businessId) {
      fetchMembers();
    }
  }, [businessId]);

  useEffect(() => {
    if (selectedMember) {
      fetchMeasurements(selectedMember.member_id);
    }
  }, [selectedMember]);

  const fetchMembers = async () => {
    try {
      const [membersRes, paramsRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/members`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/enabled-measurement-params`, { credentials: "include" }),
      ]);
      
      if (membersRes.ok) {
        setMembers(await membersRes.json());
      }
      if (paramsRes.ok) {
        const data = await paramsRes.json();
        setMeasurementParams(data.params || []);
        // Set default selected param if we have params
        if (data.params?.length > 0 && !selectedParam) {
          setSelectedParam(data.params[0].name);
        }
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeasurements = async (memberId) => {
    try {
      const response = await fetch(`${API}/members/${memberId}/measurements`, {
        credentials: "include",
      });
      if (response.ok) {
        setMeasurements(await response.json());
      }
    } catch (error) {
      console.error("Error fetching measurements:", error);
    }
  };

  const handleAddMeasurement = async () => {
    if (!selectedMember || !measurementValue || !selectedParam) {
      toast.error(t('members:paramAndValueRequired'));
      return;
    }

    const param = measurementParams.find((p) => p.name === selectedParam);
    if (!param) {
      toast.error(t("common:toast_gecersizParametre"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const response = await fetch(`${API}/measurements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_id: selectedMember.member_id,
          parameter: param.name,
          value: parseFloat(measurementValue),
          unit: param.unit,
        }),
      });

      if (response.ok) {
        toast.success(t("evaluations:measurementSaved"));
        fetchMeasurements(selectedMember.member_id);
        setMeasurementValue("");
        setShowModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete measurement
  const handleDeleteMeasurement = async (measurementId) => {
    try {
      const response = await fetch(`${API}/measurements/${measurementId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t('members:measurementDeleted'));
        if (selectedMember) {
          fetchMeasurements(selectedMember.member_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const filteredMembers = members.filter((m) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      m.name?.toLowerCase().includes(query) ||
      m.surname?.toLowerCase().includes(query)
    );
  });

  const getChartData = (paramName) => {
    return measurements
      .filter((m) => m.parameter === paramName)
      .filter((m) => m.measured_at && !isNaN(new Date(m.measured_at).getTime()))
      .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at))
      .map((m) => ({
        date: format(new Date(m.measured_at), "dd/MM", { locale: getDateLocale() }),
        value: m.value,
      }));
  };

  const getLatestMeasurement = (paramName) => {
    const paramMeasurements = measurements
      .filter((m) => m.parameter === paramName)
      .filter((m) => m.measured_at && !isNaN(new Date(m.measured_at).getTime()));
    if (paramMeasurements.length === 0) return null;
    return paramMeasurements.sort(
      (a, b) => new Date(b.measured_at) - new Date(a.measured_at)
    )[0];
  };

  const getProgress = (paramName) => {
    const paramMeasurements = measurements
      .filter((m) => m.parameter === paramName)
      .filter((m) => m.measured_at && !isNaN(new Date(m.measured_at).getTime()))
      .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at));
    
    if (paramMeasurements.length < 2) return null;
    
    const first = paramMeasurements[0].value;
    const last = paramMeasurements[paramMeasurements.length - 1].value;
    const diff = last - first;
    const percent = ((diff / first) * 100).toFixed(1);
    
    return { diff, percent, isPositive: diff > 0, first, last };
  };

  // Tüm parametrelerin son değerlerini grupla
  const getUniqueParams = () => {
    return [...new Set(measurements.map(m => m.parameter))];
  };

  // Timeline görünümü için ölçümleri tarihe göre grupla
  const getTimelineData = () => {
    const sorted = [...measurements]
      .filter((m) => m.measured_at && !isNaN(new Date(m.measured_at).getTime()))
      .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at));
    
    // Tarihe göre grupla
    const grouped = {};
    sorted.forEach(m => {
      const dateKey = format(new Date(m.measured_at), "yyyy-MM-dd");
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(m);
    });
    
    return Object.entries(grouped).map(([date, items]) => ({
      date,
      dateFormatted: format(new Date(date), "d MMMM yyyy, EEEE", { locale: getDateLocale() }),
      items
    }));
  };

  // Karşılaştırma tablosu için veri
  const getComparisonData = () => {
    const params = getUniqueParams();
    return params.map(param => {
      const paramMeasurements = measurements
        .filter(m => m.parameter === param)
        .filter(m => m.measured_at && !isNaN(new Date(m.measured_at).getTime()))
        .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at));
      
      const first = paramMeasurements[0];
      const last = paramMeasurements[paramMeasurements.length - 1];
      const progress = getProgress(param);
      
      return {
        parameter: param,
        unit: first?.unit || "",
        firstValue: first?.value,
        firstDate: first?.measured_at,
        lastValue: last?.value,
        lastDate: last?.measured_at,
        progress,
        count: paramMeasurements.length
      };
    });
  };

  // TIMELINE GÖRÜNÜMÜ
  const renderTimelineView = () => {
    const timelineData = getTimelineData();
    
    if (timelineData.length === 0) {
      return (
        <div className="text-center py-12 text-grey">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t("evaluations:noMeasurementsYet")}</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {timelineData.map((day, dayIdx) => (
          <div key={day.date} className="relative">
            {/* Timeline line */}
            {dayIdx < timelineData.length - 1 && (
              <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gradient-to-b from-navy/30 to-transparent" />
            )}
            
            {/* Date header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-navy">{day.dateFormatted}</h4>
            </div>
            
            {/* Measurements for this day */}
            <div className="ml-11 space-y-2">
              {day.items.map((m) => {
                const progress = getProgress(m.parameter);
                return (
                  <div
                    key={m.measurement_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl group hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                        <Activity className="w-5 h-5 text-navy" />
                      </div>
                      <div>
                        <p className="font-medium text-navy">{getLocalizedMeasurement(m.parameter)}</p>
                        <p className="text-xs text-grey">
                          {format(new Date(m.measured_at), "HH:mm", { locale: getDateLocale() })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-lg text-navy">
                          {m.value} <span className="text-sm font-normal text-grey">{m.unit}</span>
                        </p>
                        {progress && (
                          <div className={`flex items-center gap-1 text-xs ${
                            progress.isPositive ? "text-red-600" : "text-green-600"
                          }`}>
                            {progress.isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                            <span>{progress.percent}% toplam</span>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:bg-red-50 h-8 w-8"
                        onClick={() => handleDeleteMeasurement(m.measurement_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // KARŞILAŞTIRMA TABLOSU GÖRÜNÜMÜ
  const renderTableView = () => {
    const comparisonData = getComparisonData();
    
    if (comparisonData.length === 0) {
      return (
        <div className="text-center py-12 text-grey">
          <Table2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t("evaluations:noMeasurementsYet")}</p>
        </div>
      );
    }
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 font-semibold text-navy">Parametre</th>
              <th className="text-center py-3 px-4 font-semibold text-navy">İlk Ölçüm</th>
              <th className="text-center py-3 px-4 font-semibold text-navy">Son Ölçüm</th>
              <th className="text-center py-3 px-4 font-semibold text-navy">Değişim</th>
              <th className="text-center py-3 px-4 font-semibold text-navy">Ölçüm Sayısı</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((row, idx) => (
              <tr key={row.parameter} className={idx % 2 === 0 ? "bg-slate-50" : ""}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-navy" />
                    <span className="font-medium">{row.parameter}</span>
                    <span className="text-grey text-sm">({row.unit})</span>
                  </div>
                </td>
                <td className="text-center py-3 px-4">
                  {row.firstValue !== undefined ? (
                    <div>
                      <p className="font-semibold">{row.firstValue} {row.unit}</p>
                      <p className="text-xs text-grey">
                        {row.firstDate && format(new Date(row.firstDate), "dd/MM/yy")}
                      </p>
                    </div>
                  ) : "-"}
                </td>
                <td className="text-center py-3 px-4">
                  {row.lastValue !== undefined ? (
                    <div>
                      <p className="font-semibold">{row.lastValue} {row.unit}</p>
                      <p className="text-xs text-grey">
                        {row.lastDate && format(new Date(row.lastDate), "dd/MM/yy")}
                      </p>
                    </div>
                  ) : "-"}
                </td>
                <td className="text-center py-3 px-4">
                  {row.progress ? (
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                      row.progress.isPositive 
                        ? "bg-red-100 text-red-700" 
                        : "bg-green-100 text-green-700"
                    }`}>
                      {row.progress.isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      {row.progress.diff > 0 ? "+" : ""}{row.progress.diff.toFixed(1)} ({row.progress.percent}%)
                    </div>
                  ) : (
                    <span className="text-grey">-</span>
                  )}
                </td>
                <td className="text-center py-3 px-4">
                  <Badge variant="secondary">{row.count}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // GRAFİK + KARTLAR GÖRÜNÜMÜ
  const renderGraphView = () => {
    const params = getUniqueParams();
    
    if (params.length === 0) {
      return (
        <div className="text-center py-12 text-grey">
          <LineChartIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t("evaluations:noMeasurementsYet")}</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Ana Grafik */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <CardTitle className="text-base font-medium">{t("evaluations:olcumGrafigi")}</CardTitle>
              <Select value={selectedChartParam || params[0]} onValueChange={setSelectedChartParam}>
                <SelectTrigger className="w-[200px] rounded-xl">
                  <SelectValue placeholder={t("common:ph_parametreSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {params.map((param) => {
                    const m = measurements.find(x => x.parameter === param);
                    return (
                      <SelectItem key={param} value={param}>
                        {param} {m?.unit ? `(${m.unit})` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {(selectedChartParam || params[0]) && getChartData(selectedChartParam || params[0]).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={getChartData(selectedChartParam || params[0])}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1B365D" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1B365D" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#1B365D"
                    strokeWidth={2}
                    fill="url(#colorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-grey">
                Veri bulunamadı
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Parametre Kartları */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {params.map((param) => {
            const latest = getLatestMeasurement(param);
            const progress = getProgress(param);
            const chartData = getChartData(param).slice(-5);
            
            return (
              <Card key={param} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-sm text-grey">{param}</p>
                      <p className="text-2xl font-bold text-navy">
                        {latest?.value} <span className="text-sm font-normal">{latest?.unit}</span>
                      </p>
                    </div>
                    {progress && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        progress.isPositive ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                      }`}>
                        {progress.isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {progress.percent}%
                      </div>
                    )}
                  </div>
                  
                  {/* Mini chart */}
                  {chartData.length > 1 && (
                    <div className="h-16">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#1B365D"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  
                  {latest && (
                    <p className="text-xs text-grey mt-2">
                      Son: {format(new Date(latest.measured_at), "dd/MM/yy HH:mm")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // İLERLEME KARTLARI GÖRÜNÜMÜ
  const renderProgressView = () => {
    const comparisonData = getComparisonData();
    
    if (comparisonData.length === 0) {
      return (
        <div className="text-center py-12 text-grey">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t("evaluations:noMeasurementsYet")}</p>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {comparisonData.map((item) => {
          const chartData = getChartData(item.parameter);
          const progressPercent = item.progress ? Math.min(Math.abs(parseFloat(item.progress.percent)), 100) : 0;
          
          return (
            <Card key={item.parameter} className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-navy/10 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-navy" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy">{item.parameter}</h4>
                        <p className="text-sm text-grey">{item.count} ölçüm</p>
                      </div>
                    </div>
                    {item.progress && (
                      <div className={`text-right ${
                        item.progress.isPositive ? "text-red-600" : "text-green-600"
                      }`}>
                        <div className="flex items-center gap-1 justify-end">
                          {item.progress.isPositive ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                          <span className="font-bold text-lg">{item.progress.percent}%</span>
                        </div>
                        <p className="text-xs">
                          {item.progress.diff > 0 ? "+" : ""}{item.progress.diff.toFixed(1)} {item.unit}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* İlerleme çubuğu */}
                  {item.progress && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-grey">Başlangıç: {item.firstValue} {item.unit}</span>
                        <span className="font-medium text-navy">Şu an: {item.lastValue} {item.unit}</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            item.progress.isPositive ? "bg-red-500" : "bg-green-500"
                          }`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Mini bar chart */}
                  {chartData.length > 0 && (
                    <div className="h-20 -mx-4 -mb-4 bg-slate-50 px-4 pt-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData.slice(-7)}>
                          <Bar 
                            dataKey="value" 
                            fill="#1B365D" 
                            radius={[2, 2, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("evaluations:measurements")}</h2>
          <p className="text-grey text-sm">{t("common:uyeGelisimTakibi")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Members List */}
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">{t('members:title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
              <Input
                placeholder={t('members:searchMember')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 rounded-xl text-sm"
              />
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredMembers.map((member) => (
                <button
                  key={member.member_id}
                  className={`w-full p-3 rounded-xl flex items-center gap-3 transition-colors ${
                    selectedMember?.member_id === member.member_id
                      ? "bg-navy text-white"
                      : "bg-slate-50 hover:bg-slate-100"
                  }`}
                  onClick={() => setSelectedMember(member)}
                  data-testid={`select-member-${member.member_id}`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback
                      className={
                        selectedMember?.member_id === member.member_id
                          ? "bg-white/20 text-white"
                          : "bg-navy text-white"
                      }
                    >
                      {member.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-medium text-sm">
                      {member.name} {member.surname}
                    </p>
                    <p
                      className={`text-xs ${
                        selectedMember?.member_id === member.member_id
                          ? "text-white/70"
                          : "text-grey"
                      }`}
                    >
                      {member.member_no}
                    </p>
                  </div>
                </button>
              ))}

              {filteredMembers.length === 0 && (
                <p className="text-center text-grey text-sm py-4">{t("common:memberNotFound")}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Measurements Detail */}
        <div className="lg:col-span-2 space-y-6">
          {selectedMember ? (
            <>
              {/* Member Info & Add Button */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-navy text-white">
                          {selectedMember.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-heading font-semibold text-navy">
                          {selectedMember.name} {selectedMember.surname}
                        </h3>
                        <p className="text-sm text-grey">{selectedMember.member_no}</p>
                      </div>
                    </div>

                    <Dialog open={showModal} onOpenChange={setShowModal}>
                      <DialogTrigger asChild>
                        <Button
                          className="bg-navy hover:bg-navy/90 rounded-xl"
                          data-testid="add-measurement-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t("evaluations:addMeasurement")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-sm">
                        <DialogHeader>
                          <DialogTitle>{t("evaluations:yeniOlcum")}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Parametre</Label>
                            <Select value={selectedParam} onValueChange={setSelectedParam}>
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder={t("common:ph_parametreSecin")} />
                              </SelectTrigger>
                              <SelectContent>
                                {measurementParams.length > 0 ? (
                                  measurementParams.map((param) => (
                                    <SelectItem key={param.id || param.name} value={param.name}>
                                      {getLocalizedMeasurement(param.name)} ({param.unit})
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="none" disabled>{t("admin:noActiveParamsInBusiness")}</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            {measurementParams.length === 0 && (
                              <p className="text-xs text-orange-600 mt-1">{t("evaluations:ayarlarSayfasindanOlcumParametreleriEkle")}</p>
                            )}
                          </div>

                          <div>
                            <Label>
                              Değer {measurementParams.find((p) => p.name === selectedParam)?.unit && `(${measurementParams.find((p) => p.name === selectedParam).unit})`}
                            </Label>
                            <Input
                              type="number"
                              placeholder="0"
                              value={measurementValue}
                              onChange={(e) => setMeasurementValue(e.target.value)}
                              className="rounded-xl"
                              data-testid="measurement-value-input"
                            />
                          </div>

                          <Button
                            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                            onClick={handleAddMeasurement}
                            disabled={measurementParams.length === 0 || !selectedParam || isSaving}
                          >
                            {isSaving ? t("common:saving") : t('common:save')}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>

              {/* Görünüm Seçici */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h3 className="font-semibold text-navy">{t("evaluations:olcumVerileri")}</h3>
                    <div className="flex gap-2">
                      <Button
                        variant={viewMode === "timeline" ? "default" : "outline"}
                        size="sm"
                        className={`rounded-xl ${viewMode === "timeline" ? "bg-navy" : ""}`}
                        onClick={() => setViewMode("timeline")}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Timeline</span>
                      </Button>
                      <Button
                        variant={viewMode === "table" ? "default" : "outline"}
                        size="sm"
                        className={`rounded-xl ${viewMode === "table" ? "bg-navy" : ""}`}
                        onClick={() => setViewMode("table")}
                      >
                        <Table2 className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t("common:table")}</span>
                      </Button>
                      <Button
                        variant={viewMode === "graph" ? "default" : "outline"}
                        size="sm"
                        className={`rounded-xl ${viewMode === "graph" ? "bg-navy" : ""}`}
                        onClick={() => setViewMode("graph")}
                      >
                        <LineChartIcon className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t("common:chart")}</span>
                      </Button>
                      <Button
                        variant={viewMode === "progress" ? "default" : "outline"}
                        size="sm"
                        className={`rounded-xl ${viewMode === "progress" ? "bg-navy" : ""}`}
                        onClick={() => setViewMode("progress")}
                      >
                        <Target className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t("common:i̇lerleme")}</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Seçilen Görünüm */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  {viewMode === "timeline" && renderTimelineView()}
                  {viewMode === "table" && renderTableView()}
                  {viewMode === "graph" && renderGraphView()}
                  {viewMode === "progress" && renderProgressView()}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Activity className="w-12 h-12 text-grey mx-auto mb-3" />
                <p className="text-grey">{t("evaluations:olcumGormekIcinBirUye")}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
